import { useState } from 'react';
import { C } from './constants';
import { loadShift, createShift, isFirebaseConfigured, migrateIfNeeded } from './firebase';
import { SAMPLES } from './data';
import FAQView from './views/FAQView';
import { DTMS } from './constants';


export default function ShiftGate({ onJoin }) {
  const [mode,         setMode]         = useState('home'); // 'home' | 'create' | 'join'
  const [joinCode,     setJoinCode]     = useState('');
  const [joinPin,      setJoinPin]      = useState('');
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [preview,      setPreview]      = useState('');
  const [previewPin,   setPreviewPin]   = useState('');
  const [createdShift, setCreatedShift] = useState(null);
  const [copied,       setCopied]       = useState(false);
  const [pendingShift, setPendingShift] = useState(null);
  const [nameVal,      setNameVal]      = useState(() => localStorage.getItem('hpwa_user') || '');
  const [nameError,    setNameError]    = useState('');
  const [showFaq,      setShowFaq]      = useState(false);

  const configured = isFirebaseConfigured();

  // Which panel is visible — drives display:none toggling (no unmount = no flicker)
  const panel = pendingShift ? 'name' : createdShift ? 'share' : 'main';

  const enterWithName = () => {
    const trimmed = nameVal.trim();
    if (!trimmed) { setNameError('Please enter your name to continue.'); return; }
    localStorage.setItem('hpwa_user', trimmed);
    onJoin(pendingShift.code, pendingShift.patients, pendingShift.teams, trimmed, pendingShift.subspecs);
  };

  const handleCreate = async () => {
    const code = preview.trim();
    const pin  = previewPin.trim();
    if (code.length < 3) { setError('Code must be at least 3 characters.'); return; }
    if (pin.length !== 4) { setError('PIN must be exactly 4 digits.'); return; }
    setLoading(true); setError('');
    try {
      if (configured) {
        const existing = await loadShift(code);
        if (existing) {
          setError(`"${code}" already exists. Choose a different code.`);
          setLoading(false);
          return;
        }
        await createShift(code, [], DTMS, pin);
      }
      setCreatedShift({ code, pin, patients: [], teams: DTMS });
    } catch { setError('Could not create database. Check your internet connection.'); }
    setLoading(false);
  };

  const handleEnterShift = () => { if (createdShift) setPendingShift(createdShift); };

  const handleJoin = async () => {
    const trimmed = joinCode.toUpperCase().trim();
    const pin     = joinPin.trim();
    if (!trimmed) { setError('Please enter a database code.'); return; }
    setLoading(true); setError('');
    try {
      if (configured) {
        await migrateIfNeeded(trimmed);
        const data = await loadShift(trimmed);
        if (!data) { setError(`"${trimmed}" not found. Check the code and try again.`); setLoading(false); return; }
        // PIN check — if database has a PIN, verify it
        if (data.pinHash) {
          if (!pin) { setError('This database requires a PIN.'); setLoading(false); return; }
          if (btoa(pin) !== data.pinHash) { setError('Incorrect PIN. Try again.'); setLoading(false); return; }
        }
        setPendingShift({ code: trimmed, patients: data.patients || [], teams: data.teams || DTMS, subspecs: data.subspecs || [] });
      } else {
        setPendingShift({ code: trimmed, patients: SAMPLES, teams: DTMS });
      }
    } catch { setError('Could not connect. Check your internet connection.'); }
    setLoading(false);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputStyle = {
    width: '100%', background: C.bg3, border: `1.5px solid ${C.bd}`,
    borderRadius: 10, padding: '12px 14px', color: C.tx,
    fontSize: 18, fontWeight: 700, letterSpacing: '0.12em',
    textTransform: 'uppercase', outline: 'none', textAlign: 'center',
  };
  const btnStyle = (bg, color, border) => ({
    width: '100%', padding: '13px 0', borderRadius: 12, cursor: loading ? 'not-allowed' : 'pointer',
    background: loading ? C.bg2 : bg, color: loading ? C.dim : color,
    border: border || 'none', fontSize: 15, fontWeight: 700, marginBottom: 10,
    opacity: loading ? 0.6 : 1,
  });

  const shareMsg = createdShift ? `Join my Patient Tracker database!\n\nDatabase code: ${createdShift.code}\nPIN: ${createdShift.pin}\n\nTap "Join Existing Database", enter the code and PIN.` : '';

  // Single return — all panels always mounted, visibility toggled via display
  // This prevents iOS from tearing down compositing layers between state changes
  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg0, WebkitTransform: 'translateZ(0)', transform: 'translateZ(0)' }}>

      {/* ── NAME GATE ── */}
      <div style={{ display: panel === 'name' ? 'flex' : 'none', position: 'absolute', inset: 0, justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, padding: '24px 24px max(env(safe-area-inset-bottom), 24px)' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>👤</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#E8F4FF' }}>Who are you?</div>
            <div style={{ fontSize: 13, color: C.dim, marginTop: 6 }}>Your name will appear in the audit log and messages</div>
            <div style={{ fontSize: 12, color: '#4A9EFF', marginTop: 4, fontWeight: 700, letterSpacing: '0.1em' }}>Database {pendingShift?.code}</div>
          </div>
          <input
            autoFocus={panel === 'name'}
            value={nameVal}
            onChange={e => { setNameVal(e.target.value); setNameError(''); }}
            onKeyDown={e => e.key === 'Enter' && enterWithName()}
            placeholder="e.g. Dr Smith"
            maxLength={40}
            style={{ width: '100%', background: C.bg3, border: `1.5px solid ${nameError ? '#FF4545' : C.bd}`, borderRadius: 10, padding: '14px 16px', color: C.tx, fontSize: 18, fontWeight: 600, outline: 'none', textAlign: 'center', boxSizing: 'border-box', marginBottom: 8 }}
          />
          {nameError && <div style={{ color: '#FF4545', fontSize: 12, textAlign: 'center', marginBottom: 8 }}>{nameError}</div>}
          <button onClick={enterWithName} style={{ width: '100%', padding: '14px 0', borderRadius: 12, background: '#1A3D6A', border: '1px solid #4A9EFF55', color: '#7DC4FF', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}>→ Enter Database</button>
          <button onClick={() => setPendingShift(null)} style={{ width: '100%', padding: '11px 0', borderRadius: 12, background: 'transparent', border: `1px solid ${C.bd}`, color: C.dim, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
        </div>
      </div>

      {/* ── SHARE SHEET ── */}
      <div style={{ display: panel === 'share' ? 'flex' : 'none', position: 'absolute', inset: 0, justifyContent: 'center', alignItems: 'flex-end' }}>
        <div style={{ width: '100%', maxWidth: 480, background: C.bg1, borderRadius: '24px 24px 0 0', padding: '28px 24px max(44px, calc(env(safe-area-inset-bottom) + 24px))', boxShadow: '0 -8px 40px rgba(0,0,0,0.5)' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.dim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Database Created! Share your code</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Code</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#4A9EFF', letterSpacing: '0.15em', background: C.bg2, borderRadius: 12, padding: '14px 0', border: `1px solid ${C.bd}` }}>{createdShift?.code}</div>
              </div>
              <div style={{ width: 100 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>PIN</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#FF9500', letterSpacing: '0.2em', background: C.bg2, borderRadius: 12, padding: '14px 0', border: `1px solid ${C.bd}` }}>{createdShift?.pin}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.dim, marginTop: 8 }}>Share both the code and PIN with your team</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {typeof navigator.share === 'function' && (
              <button onClick={() => navigator.share({ title: 'Patient Tracker Database Code', text: shareMsg }).catch(() => {})}
                style={{ padding: '14px 0', borderRadius: 12, background: '#1A3D6A', border: '1px solid #4A9EFF55', color: '#7DC4FF', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>📤 Share via…</button>
            )}
            <button onClick={() => handleCopyCode(createdShift?.code || '')}
              style={{ padding: '14px 0', borderRadius: 12, background: copied ? '#081A0C' : C.bg2, border: `1px solid ${copied ? '#30D158' : C.bd}`, color: copied ? '#30D158' : C.tx, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {copied ? '✓ Copied!' : '📋 Copy Code'}
            </button>
            <a href={`sms:?body=${encodeURIComponent(shareMsg)}`} style={{ padding: '14px 0', borderRadius: 12, background: '#0A2A0A', border: '1px solid #1A4A1A', color: '#30D158', fontSize: 14, fontWeight: 600, textDecoration: 'none', textAlign: 'center', display: 'block' }}>💬 Send via SMS</a>
            <a href={`mailto:?subject=${encodeURIComponent('Patient Tracker — Database Code')}&body=${encodeURIComponent(shareMsg)}`} style={{ padding: '14px 0', borderRadius: 12, background: '#0A1A2A', border: '1px solid #1A3A4A', color: '#4A9EFF', fontSize: 14, fontWeight: 600, textDecoration: 'none', textAlign: 'center', display: 'block' }}>✉️ Send via Email</a>
          </div>
          <button onClick={handleEnterShift} style={{ width: '100%', padding: '15px 0', borderRadius: 14, background: '#0D2A0D', border: '1.5px solid #30D158', color: '#30D158', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>→ Enter Database</button>
        </div>
      </div>

      {/* ── HOME / CREATE / JOIN ── */}
      <div style={{ display: panel === 'main' ? 'flex' : 'none', position: 'absolute', inset: 0, justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, padding: 'max(env(safe-area-inset-top), 24px) 24px max(env(safe-area-inset-bottom), 24px)' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>🏥</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#E8F4FF', letterSpacing: '-0.02em' }}>Patient Tracker</div>
            <div style={{ fontSize: 13, color: C.dim, marginTop: 4 }}>Clinical handoff · Real-time sync</div>
          </div>
          {!configured && (
            <div style={{ background: '#1A1500', border: '1px solid #FF950044', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#FF9500', lineHeight: 1.5 }}>
              ⚠️ Firebase not configured — data won't sync across devices yet.<br />See <code style={{ fontSize: 11 }}>src/firebase.js</code> for setup instructions.
            </div>
          )}

          {/* HOME */}
          {mode === 'home' && (
            <>
              <button onClick={() => { setMode('create'); setPreview(''); }} style={btnStyle('#1A3D6A', '#7DC4FF')}>+ Create New Database</button>
              <button onClick={() => setMode('join')} style={btnStyle('transparent', '#4A9EFF', `1.5px solid #4A9EFF44`)}>→ Join Existing Database</button>

              <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: C.dim2, lineHeight: 1.6 }}>
                Each database gets a unique code + PIN.<br />Share both with your team so everyone sees the same patients.
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 18 }}>
                <button onClick={() => setPendingShift({ code: 'DEMO', patients: SAMPLES, teams: DTMS })}
                  style={{ background: 'none', border: 'none', color: C.dim2, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: C.dim2 }}>try demo</button>
                <span style={{ color: C.dim2, fontSize: 10 }}>·</span>
                <button onClick={() => setShowFaq(true)}
                  style={{ background: 'none', border: 'none', color: C.dim2, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: C.dim2 }}>help & faq</button>
              </div>

              {/* Support email */}
              <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: C.dim2 }}>
                Questions? <a href="mailto:dpo@pxtracker.cc" style={{ color: C.dim2, textDecoration: 'underline', textDecorationColor: C.dim2 }}>dpo@pxtracker.cc</a>
              </div>
            </>
          )}

          {/* CREATE */}
          {mode === 'create' && (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.dim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Database code</div>
                <input
                  value={preview}
                  onChange={e => { setPreview(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 12)); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && preview.trim().length >= 3 && previewPin.length === 4 && handleCreate()}
                  placeholder="e.g. IMICU-2026"
                  maxLength={12}
                  autoFocus
                  style={{ ...inputStyle, fontSize: 22, fontWeight: 800, letterSpacing: '0.15em' }}
                />
                <div style={{ fontSize: 11, color: C.dim2, marginTop: 5 }}>Letters, numbers and hyphens · 3–12 characters</div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.dim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>PIN <span style={{ color: C.dim2, fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>— 4 digits</span></div>
                <input
                  value={previewPin}
                  onChange={e => { setPreviewPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && preview.trim().length >= 3 && previewPin.length === 4 && handleCreate()}
                  placeholder="e.g. 4821"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ ...inputStyle, fontSize: 28, letterSpacing: '0.35em' }}
                />
              </div>
              <div style={{ background: C.bg2, borderRadius: 10, padding: '10px 14px', marginBottom: 16, border: `1px solid ${C.bd}` }}>
                <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.7 }}>📋 Share both code + PIN with your team<br />🔒 PIN required to join — keep it private<br />🔄 Everyone sees the same patients in real time</div>
              </div>
              {error && <div style={{ color: '#FF4545', fontSize: 12, textAlign: 'center', marginBottom: 12 }}>{error}</div>}
              <button onClick={handleCreate} disabled={loading || preview.trim().length < 3 || previewPin.length !== 4} style={btnStyle('#1A3D6A', '#7DC4FF', null)}>{loading ? 'Creating…' : 'Create Database'}</button>
              <button onClick={() => { setMode('home'); setError(''); }} style={btnStyle('transparent', C.dim, `1px solid ${C.bd}`)}>← Back</button>
            </>
          )}

          {/* JOIN */}
          {mode === 'join' && (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.dim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Database code</div>
                <input
                  value={joinCode}
                  onChange={e => { setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 12)); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleJoin()}
                  placeholder="e.g. IMICU-2026"
                  maxLength={12}
                  autoFocus
                  style={{ ...inputStyle, fontSize: 22, fontWeight: 800, letterSpacing: '0.15em' }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.dim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>PIN</div>
                <input
                  value={joinPin}
                  onChange={e => { setJoinPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleJoin()}
                  placeholder="4 digits"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ ...inputStyle, fontSize: 28, letterSpacing: '0.35em' }}
                />
              </div>
              {error && <div style={{ color: '#FF4545', fontSize: 12, textAlign: 'center', marginBottom: 12 }}>{error}</div>}
              <button onClick={handleJoin} disabled={loading} style={btnStyle('#1A3D6A', '#7DC4FF')}>{loading ? 'Joining…' : '→ Join Database'}</button>
              <button onClick={() => { setMode('home'); setError(''); setJoinCode(''); setJoinPin(''); }} style={btnStyle('transparent', C.dim, `1px solid ${C.bd}`)}>← Back</button>
              <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: C.dim2 }}>Ask your team lead for the code and PIN</div>
            </>
          )}
        </div>
      </div>

      {showFaq && <FAQView onClose={() => setShowFaq(false)} />}
    </div>
  );
}
